export enum ApiErrorType {
  UnexpectedResponse,
  ClientError,
  ServerError,
  Unauthorized,
  UnexpectedException,
  ValidationError,
  NetworkError,
  RateLimitError,
  MaintenanceError,
}

export enum ErrorSeverity {
  Low = "low",        // Info/warnings, non-blocking
  Medium = "medium",  // Feature unavailable, workarounds exist
  High = "high",      // Core functionality broken
  Critical = "critical", // Data loss risk, immediate attention needed
}

export enum ErrorCategory {
  Network = "network",
  Validation = "validation", 
  Authentication = "authentication",
  Authorization = "authorization",
  Server = "server",
  Client = "client",
  Unknown = "unknown",
}

export interface ErrorContext {
  action?: string;           // What the user was trying to do
  component?: string;        // Which component the error occurred in
  userId?: string;          // For debugging user-specific issues
  timestamp?: Date;         // When the error occurred
  sessionId?: string;       // For correlating related errors
  metadata?: Record<string, any>; // Additional context
}

export interface ErrorRecovery {
  canRetry?: boolean;
  retryAction?: () => Promise<void>;
  alternativeAction?: {
    label: string;
    action: () => void;
  };
  helpUrl?: string;
  contactSupport?: boolean;
}

export class ApiError extends Error {
  type: ApiErrorType;
  severity: ErrorSeverity;
  category: ErrorCategory;
  status?: number;
  originalError?: unknown;
  context?: ErrorContext;
  recovery?: ErrorRecovery;
  userMessage?: string;      // User-friendly message
  developerMessage?: string; // Technical details for debugging
  errorCode?: string;        // Unique identifier for this error type

  constructor(
    message: string, 
    type: ApiErrorType, 
    options: {
      status?: number;
      originalError?: unknown;
      severity?: ErrorSeverity;
      category?: ErrorCategory;
      context?: ErrorContext;
      recovery?: ErrorRecovery;
      userMessage?: string;
      developerMessage?: string;
      errorCode?: string;
    } = {}
  ) {
    super(message);
    this.type = type;
    this.status = options.status;
    this.originalError = options.originalError;
    this.severity = options.severity || ErrorSeverity.Medium;
    this.category = options.category || ErrorCategory.Unknown;
    this.context = options.context;
    this.recovery = options.recovery;
    this.userMessage = options.userMessage || message;
    this.developerMessage = options.developerMessage;
    this.errorCode = options.errorCode;
  }
}

/**
 * Create a validation error for form fields
 */
export function createValidationError(
  fieldErrors: Record<string, string>,
  message: string = "Please correct the errors below",
  context?: ErrorContext
): ApiError {
  return new ApiError(message, ApiErrorType.ValidationError, {
    severity: ErrorSeverity.Low,
    category: ErrorCategory.Validation,
    context: {
      ...context,
      metadata: { fieldErrors },
    },
    userMessage: message,
    developerMessage: `Validation failed: ${JSON.stringify(fieldErrors)}`,
    errorCode: "VALIDATION_ERROR",
    recovery: {
      canRetry: false,
      helpUrl: "/help/form-validation",
    },
  });
}

/**
 * Utility to determine if an error is recoverable
 */
export function isRecoverableError(error: ApiError): boolean {
  return error.recovery?.canRetry === true || 
         !!error.recovery?.alternativeAction ||
         error.category === ErrorCategory.Network;
}

/**
 * Utility to get user-friendly error message
 */
export function getUserFriendlyMessage(error: ApiError): string {
  return error.userMessage || error.message || "An unexpected error occurred.";
}

/**
 * Simple replacement for handleApiError - just throws ApiError instead of emitting to event bus
 */
export function handleApiError(response: any, expectedStatus: number, customMessage?: string): void {
  if (response?.status === expectedStatus) return;
  
  throw new ApiError(customMessage || `Unexpected status code: ${response?.status}`, ApiErrorType.UnexpectedResponse, {
    status: response?.status,
    userMessage: customMessage || "Something went wrong. Please try again."
  });
}

/**
 * Simple replacement for handleException - just throws ApiError instead of emitting to event bus
 */
export function handleException(error: unknown, customMessage?: string): never {
  throw new ApiError(customMessage || "Unexpected error occurred", ApiErrorType.UnexpectedException, {
    originalError: error,
    userMessage: customMessage || "Something unexpected happened. Please try again."
  });
}
